import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Box, Tabs, Tab } from "@mui/material";
import { resourceCentreActions, ResourceCentreActionsType } from "../../actions";
import { AddonsSettings } from "./Settings/AddonsSettings";
import HmisSettings from "./Settings/HmisSettings";
import OkhatiAiSettings from "./Settings/OkhatiAiSettings";
import { ResourceCentre } from "../../interfaces/ResourceCentreInterface";
import NepalPayForm from "./Settings/PaymentSettings/NepalPayForm";
import { FonepayForm } from "./Settings/PaymentSettings/FonepayForm";
import { resolveOkhatiAiSettings } from "../../hooks/useOkhatiAiSettings";

interface SettingsProps {
  resourceCentre: ResourceCentre;
  actions: ResourceCentreActionsType;
}

export const DEFAULT_SETTINGS = {
  settings: {
    clientSettings: {
      enablePictureUpload: false,
      enableLeadClients: false,
      sendAppLinkToClient: false
    },
    assessmentSettings: {
      attachmentCount: 3,
      components: {
        attachments: true
      }
    },
    labSettings: {
      attachmentCount: 3
    },
    hmisSettings: {
      username: "",
      password: "",
      orgUnit: "",
      hmisFormDataIds: [],
      government: false
    },
    billingSettings: {},
    okhatiAiSettings: {
      enabled: false,
      transcriptionApiKey: "",
      prompt: ""
    }
  }
};

export type SettingsType = typeof DEFAULT_SETTINGS;

enum TAB_ITEMS {
  ADDONS = "Addons",
  OKHATI_AI = "Okhati AI",
  HMIS = "HMIS",
  NEPAL_PAY = "Nepal Pay",
  FONEPAY = "Fonepay"
}

const Settings: React.FC<SettingsProps> = ({ resourceCentre, actions }) => {
  const { id } = resourceCentre;
  const { hmisSettings } = resourceCentre.settings;
  const { attachments } = resourceCentre.settings.assessmentSettings.components;
  const assessmentAttachmentCount =
    resourceCentre.settings.assessmentSettings?.attachmentCount ?? 3;
  const labAttachmentCount = resourceCentre.settings.labSettings.attachmentCount ?? 3;

  const [tab, setTab] = React.useState(TAB_ITEMS.ADDONS);
  const [state, setState] = React.useState(DEFAULT_SETTINGS);

  React.useEffect(() => {
    const resolved = resolveOkhatiAiSettings(resourceCentre);

    setState({
      ...resourceCentre,
      settings: {
        ...resourceCentre.settings,
        assessmentSettings: {
          ...resourceCentre.settings.assessmentSettings,
          attachmentCount: assessmentAttachmentCount,
          components: {
            ...resourceCentre.settings.assessmentSettings.components,
            attachments
          }
        },
        labSettings: {
          ...resourceCentre.settings.labSettings,
          attachmentCount: labAttachmentCount
        },
        hmisSettings: {
          username: hmisSettings?.username,
          password: hmisSettings?.password,
          orgUnit: hmisSettings?.orgUnit,
          hmisFormDataIds: hmisSettings?.hmisFormDataIds,
          government: hmisSettings?.government
        },
        okhatiAiSettings: {
          enabled: resolved.enabled,
          transcriptionApiKey: resolved.transcriptionApiKey,
          prompt: resolved.prompt
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resourceCentre]);

  return (
    <Box width="100%">
      <Box sx={(theme) => ({ borderBottom: `1px solid ${theme.palette.divider}` })}>
        <Tabs value={tab} onChange={(_, newValue) => setTab(newValue)}>
          <Tab value={TAB_ITEMS.ADDONS} label={TAB_ITEMS.ADDONS} />
          <Tab value={TAB_ITEMS.OKHATI_AI} label={TAB_ITEMS.OKHATI_AI} />
          <Tab value={TAB_ITEMS.HMIS} label={TAB_ITEMS.HMIS} />
          <Tab value={TAB_ITEMS.NEPAL_PAY} label={TAB_ITEMS.NEPAL_PAY} />
          <Tab value={TAB_ITEMS.FONEPAY} label={TAB_ITEMS.FONEPAY} />
        </Tabs>
      </Box>

      <Box>
        {tab === TAB_ITEMS.ADDONS && (
          <AddonsSettings
            id={id}
            initialSubscriptions={resourceCentre?.subscriptions}
            state={state}
            setState={setState}
            actions={actions}
          />
        )}
        {tab === TAB_ITEMS.OKHATI_AI && (
          <OkhatiAiSettings state={state} setState={setState} actions={actions} />
        )}
        {tab === TAB_ITEMS.HMIS && <HmisSettings state={state} actions={actions} />}
        {tab === TAB_ITEMS.NEPAL_PAY && <NepalPayForm resourceCentre={resourceCentre} />}
        {tab === TAB_ITEMS.FONEPAY && <FonepayForm resourceCentre={resourceCentre} />}
      </Box>
    </Box>
  );
};

export default connect(null, (dispatch) => ({
  actions: bindActionCreators({ ...resourceCentreActions }, dispatch)
}))(Settings);
